import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
  Box,
  Switch,
  FormControlLabel,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CompareSalesView from '../../../pages/inspector/shared/Exceptions/CompareSalesView'
import { HeaderSummaryChips } from './SaleSummaryChips'
import { ExceptionDomainIcon } from '../../SystemIcons'
import inspectorApi, { CompareSalesResponse } from '../../../services/inspector/api'
import useDataFetching from '../../../hooks/useDataFetching'
import { CompareSalesDialogProps } from '../../../hooks/useCompareSalesDialog'
import { computeSaleSummary } from '../../../utils/saleSummary'
import { poolwerxColors } from '../../../theme/colors'
import {
  handleLightspeedLinkClick,
} from '../../../utils/externalLinks'

interface CompareSalesDialogComponentProps extends CompareSalesDialogProps {
  /**
   * Optional sx forwarded to the root Dialog. Used by the ExceptionDrawer
   * to lift the dialog above its side panel (zIndex modal+10) - table-
   * launched dialogs stay at the MUI default.
   */
  sx?: SxProps<Theme>
}

const CompareSalesDialog: React.FC<CompareSalesDialogComponentProps> = ({
  open,
  target,
  onClose,
  sx,
}) => {
  const { data, loading, error } = useDataFetching<CompareSalesResponse>({
    fetchFn: () => {
      if (!target) return Promise.reject(new Error('No target'))
      return inspectorApi.getCompareSales(target.fpCode, target.lsId, target.xeroInvoiceId || undefined)
    },
    dependencies: [target?.fpCode, target?.lsId, target?.xeroInvoiceId],
    immediate: !!target,
  })

  // Gate `data` to the current target so reopening with a different sale
  // doesn't flash the previous fetch's data before the new request resolves.
  const dataMatchesTarget = !!data && !!target && data.sale_ls_id === target.lsId
  const viewData = dataMatchesTarget ? data : undefined
  const viewLoading = loading || (!!target && !dataMatchesTarget)

  // Lifted from CompareSalesView so the toggle can sit in the title bar
  // alongside the summary chips.
  const [showDiffsOnly, setShowDiffsOnly] = useState(false)

  const summary = viewData ? computeSaleSummary(viewData) : null
  // Seed-then-hydrate: opened-from-row uses the row's header fields instantly;
  // a cold deep-link (no seed) falls back to the fetched response so the title
  // bar + system-panel links still populate once the request lands.
  const lsRef = target?.invoiceNumber ?? viewData?.invoice_number ?? undefined
  const lsLink =
    target?.lightspeedSaleLink ?? viewData?.lightspeed_sale_link ?? undefined
  const xeroReference =
    target?.xeroReference ?? viewData?.xero_reference ?? undefined
  const xeroInvoiceNumber =
    target?.xeroInvoiceNumber ?? viewData?.xero_invoice_number ?? undefined
  const xeroInvoiceLink =
    target?.xeroInvoiceLink ?? viewData?.xero_invoice_link ?? undefined

  const handleRefClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!lsLink) return
    handleLightspeedLinkClick(e, lsLink, `Lightspeed ${lsRef || ''}`.trim())
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      keepMounted={false}
      sx={sx}
    >
      <DialogTitle sx={{ py: 1.25, pl: 2, pr: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {/* Leading icon block - anchors the heading and ties the dialog
            * back to the sales-domain exception that opened it. */}
          <Box
            sx={{
              flexShrink: 0,
              width: 42,
              height: 42,
              borderRadius: 1.5,
              backgroundColor: poolwerxColors.tealLight,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ExceptionDomainIcon
              domain="sale"
              size={24}
              applyColor
            />
          </Box>

          {/* Title block (left). LS sale # sits inline so the analyst sees
            * what they're inspecting without scanning into the table. */}
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Compare sales
            </Typography>
            {lsRef && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lsRef}
                </Typography>
                {lsLink && (
                  <IconButton
                    component="a"
                    href={lsLink}
                    onClick={handleRefClick}
                    size="small"
                    aria-label={`Open ${lsRef} in Lightspeed`}
                    sx={{ color: 'text.secondary', p: 0.25 }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Stack>
            )}
          </Stack>

          {/* Spacer pushes the chips/toggle/close cluster to the right edge. */}
          <Box sx={{ flex: 1 }} />

          {summary && (
            <HeaderSummaryChips
              warnings={summary.warningCount}
              mismatches={summary.saleMismatches}
            />
          )}

          {summary && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showDiffsOnly}
                  onChange={(_, v) => setShowDiffsOnly(v)}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                  Show differences
                </Typography>
              }
              sx={{
                m: 0,
                backgroundColor: poolwerxColors.tealLight,
                borderRadius: 1,
                px: 1.25,
                py: 0.25,
              }}
            />
          )}

          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        {error ? (
          <Box sx={{ p: 3 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : (
          <CompareSalesView
            data={viewData}
            loading={viewLoading}
            invoiceNumber={lsRef}
            xeroReference={xeroReference}
            xeroInvoiceNumber={xeroInvoiceNumber}
            lightspeedSaleLink={lsLink}
            xeroInvoiceLink={xeroInvoiceLink}
            showDiffsOnly={showDiffsOnly}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CompareSalesDialog
